<template>
  <div class="global-search-row px-3 py-2" :class="{ selected }">
    <div class="flex align-items-center gap-2 min-w-0">
      <span class="font-semibold text-overflow-ellipsis overflow-hidden white-space-nowrap">
        {{ result.title }}
      </span>
      <span class="identifier">[{{ t("global.id") }}: {{ displayId }}]</span>
      <span class="result-type text-sm">{{ t(resultTypeKey) }}</span>
    </div>
    <div v-if="result.context || result.matchContext" class="result-details text-sm mt-1 min-w-0">
      <span v-if="result.context" class="text-color-secondary text-overflow-ellipsis">
        <strong>{{ t(contextLabelKey) }}:</strong> {{ result.context }}
      </span>
      <span v-if="result.matchContext" class="match-context text-overflow-ellipsis">
        <strong>{{ t("modules.commandMenu.v2.resultDetails.match") }}:</strong>
        {{ result.matchContext }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { GlobalSearchType } from "@/graphql";
import { useAppTranslate } from "@/language/staticTranslations";
import type { GlobalSearchResult } from "../helper/GlobalSearchNavigation";

const props = defineProps<{
  result: GlobalSearchResult;
  selected?: boolean;
}>();
const { t } = useAppTranslate();

const resultTypeKey = computed(() => {
  if (
    props.result.type === GlobalSearchType.KPI_BOARD ||
    props.result.type === GlobalSearchType.KPI_TILE
  ) {
    return `modules.commandMenu.v2.resultType.${props.result.type}`;
  }
  return `modules.commandMenu.v2.type.${props.result.type}`;
});

const displayId = computed(() => props.result.identifier ?? props.result.id);

const contextLabelKey = computed(() => {
  if (props.result.type === GlobalSearchType.SQDC_ACTION) {
    return "modules.commandMenu.v2.resultDetails.eventAndBoard";
  }
  if (props.result.type === GlobalSearchType.FILE) {
    return "modules.commandMenu.v2.resultDetails.folder";
  }
  if (
    props.result.type === GlobalSearchType.SQDC_EVENT ||
    props.result.type === GlobalSearchType.KPI_TILE
  ) {
    return "modules.commandMenu.v2.resultDetails.board";
  }
  return "modules.commandMenu.v2.resultDetails.area";
});
</script>

<style scoped lang="scss">
.global-search-row {
  border-bottom: 1px solid var(--p-surface-200);
  cursor: pointer;
  min-height: 4rem;

  &:hover,
  &.selected {
    background: var(--p-highlight-background);
    color: var(--p-highlight-color);
  }
}

.identifier {
  color: var(--p-primary-color);
  font-weight: 600;
  white-space: nowrap;
}

.result-type {
  margin-left: auto;
  padding: 0.15rem 0.5rem;
  border-radius: var(--p-border-radius-sm);
  background: var(--p-surface-100);
  color: var(--p-text-muted-color);
  white-space: nowrap;
}

.result-details {
  display: flex;
  gap: 0.5rem;
  align-items: center;

  > span:not(:last-child)::after {
    margin-left: 0.5rem;
    color: var(--p-text-muted-color);
    content: "·";
  }
}

.match-context {
  color: var(--p-primary-700);
}
</style>
